import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Shared/LandingForm.css";

/* ─────────────────────────────────────────────
   LandingForm — shared form component
   Props:
     variant       "teal" | "purple"
     title         string
     subtitle      string
     deadlineBadge string | null
     fields        array of field config objects
     submitLabel   string
     trustLine     string
     onSubmit      function(formData)
───────────────────────────────────────────── */

const COUNTRY_CODES = [
  { code: "+91",  label: "+91 🇮🇳" },
  { code: "+44",  label: "+44 🇬🇧" },
  { code: "+1",   label: "+1 🇺🇸"  },
  { code: "+971", label: "+971 🇦🇪" },
  { code: "+61",  label: "+61 🇦🇺" },
  { code: "+64",  label: "+64 🇳🇿" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[a-zA-Z\s]+$/;
const PHONE_DIGIT_REGEX = /^\d{10}$/;

// Detect a "name" field by its config name/label rather than a hardcoded type,
// since fields are passed generically from parent landing pages.
const isNameField = (field) =>
  /name/i.test(field.name || "") || /name/i.test(field.label || "");

// ⭐ NEW — Detect a WhatsApp consent checkbox by its id. All existing parent
// pages (UKDestinationPage, UniversityPage, LoanLandingPage, LPGeneral) name
// their WhatsApp consent ids with "whatsapp" in them (e.g.
// "consent-whatsapp-uk", "consent-whatsapp-uni"), so a simple id match is
// enough — no parent page needs to change.
const isWhatsappConsent = (item) => /whatsapp/i.test(item.id || "");

function FormField({ field, value, onChange }) {
  if (field.type === "phone") {
    return (
      <div className="f-row">
        <label htmlFor={`${field.name}-number`}>
          {field.label} <span className="f-required">*</span>
        </label>
        <div className="f-phone-grid">
          <select
            value={value?.code || "+91"}
            onChange={(e) => onChange(field.name, { ...value, code: e.target.value })}
            aria-label="Country code"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>
          <input
            type="tel"
            id={`${field.name}-number`}
            placeholder={field.placeholder || "98765 43210"}
            value={value?.number || ""}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
              onChange(field.name, { ...value, number: digitsOnly });
            }}
            inputMode="numeric"
            maxLength={10}
          />
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="f-row">
        <label htmlFor={field.name}>
          {field.label} <span className="f-required">*</span>
        </label>
        <select
          id={field.name}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
        >
          <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}`}</option>
          {(field.options || []).map((opt) => (
            <option key={typeof opt === "string" ? opt : opt.value}
                    value={typeof opt === "string" ? opt : opt.value}>
              {typeof opt === "string" ? opt : opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="f-row">
      <label htmlFor={field.name}>
        {field.label} <span className="f-required">*</span>
      </label>
      <input
        type={field.type || "text"}
        id={field.name}
        placeholder={field.placeholder}
        value={value || ""}
        onChange={(e) => {
          let next = e.target.value;
          if (isNameField(field)) {
            next = next.replace(/[^a-zA-Z\s]/g, "");
          }
          onChange(field.name, next);
        }}
        required
      />
    </div>
  );
}

export default function LandingForm({
  variant = "teal",
  title,
  subtitle,
  deadlineBadge,
  fields = [],
  submitLabel = "Submit",
  trustLine,
  onSubmit,
  consentItems = [],
}) {
  const [formData, setFormData] = useState({});
  const [consents, setConsents] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Auto-reset back to a blank form 5s after the success screen shows.
  // Kept in a ref so a resubmission mid-countdown (shouldn't normally
  // happen since the form is hidden once submitted, but guards against
  // rapid re-renders/StrictMode double-invoke) always clears any
  // previously scheduled timer before starting a new one.
  const resetTimerRef = useRef(null);

  useEffect(() => {
    if (submitted) {
      resetTimerRef.current = setTimeout(() => {
        setSubmitted(false);
        setFormData({});
        setConsents({});
      }, 5000);
    }
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [submitted]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsent = (id) => {
    setConsents((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ── Validation: every field mandatory. Every consent checkbox is
  // mandatory EXCEPT WhatsApp updates consent, which is optional. ──
  const validateForm = () => {
    for (const field of fields) {
      const value = formData[field.name];

      if (field.type === "phone") {
        const number = value?.number || "";
        if (!number.trim()) {
          toast.error(`Please enter your ${field.label.toLowerCase()}`);
          return false;
        }
        if (!PHONE_DIGIT_REGEX.test(number)) {
          toast.error("Phone number must be exactly 10 digits");
          return false;
        }
        continue;
      }

      if (field.type === "select") {
        if (!value) {
          toast.error(`Please select ${field.label.toLowerCase()}`);
          return false;
        }
        continue;
      }

      // text / email / default text-like fields
      if (!value || !String(value).trim()) {
        toast.error(`Please enter your ${field.label.toLowerCase()}`);
        return false;
      }

      if (isNameField(field) && !NAME_REGEX.test(String(value).trim())) {
        toast.error(`${field.label} should only contain letters`);
        return false;
      }

      if (field.type === "email" && !EMAIL_REGEX.test(value)) {
        toast.error("Please enter a valid email address");
        return false;
      }
    }

    for (const item of consentItems) {
      // ⭐ NEW — WhatsApp updates consent is optional, skip mandatory check
      if (isWhatsappConsent(item)) continue;

      if (!consents[item.id]) {
        toast.error("Please accept all consent checkboxes to proceed");
        return false;
      }
    }

    return true;
  };

const [countdown, setCountdown] = useState(5);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await onSubmit?.({ ...formData, consents });
      setSubmitted(true);
    } catch (err) {
      console.error("Form submission error:", err);
      toast.error("Error submitting enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

const countdownIntervalRef = useRef(null);

useEffect(() => {
  if (submitted) {
    setCountdown(5);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    resetTimerRef.current = setTimeout(() => {
      setSubmitted(false);
      setFormData({});
      setConsents({});
    }, 5000);
  }
  return () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };
}, [submitted]);
if (submitted) {
  return (
    <div className={`lp-form-card lp-form-card--${variant} lp-form-card--success`} aria-live="polite">
      <div className="lp-form-success__icon">✅</div>
      <h3 className="lp-form-card__title">You're all set!</h3>
      <p className="lp-form-success__msg">
        Our counsellor will reach out within 24 hours. Check your WhatsApp &amp; email.
      </p>
      <div className="lp-form-success__countdown">
        <span className="lp-spinner" aria-hidden="true" />
        <span>New enquiry in {countdown}s</span>
      </div>
    </div>
  );
}
  return (
    <div className={`lp-form-card lp-form-card--${variant}`}>
      
      <h3 className="lp-form-card__title">{title}</h3>
      {subtitle && <div className="lp-form-card__sub">{subtitle}</div>}
      {deadlineBadge && (
        <div className="lp-form-card__deadline">
          <span aria-hidden="true">⏰</span> {deadlineBadge}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {fields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={formData[field.name]}
            onChange={handleChange}
          />
        ))}

        {consentItems.length > 0 && (
          <div className="lp-consent-block">
            {consentItems.map((item) => {
              // ⭐ NEW — WhatsApp consent shows no asterisk since it's optional;
              // all other consent items keep the mandatory asterisk.
              const isWhatsapp = isWhatsappConsent(item);
              return (
                <div key={item.id} className="lp-consent-row">
                  <input
                    type="checkbox"
                    id={item.id}
                    checked={!!consents[item.id]}
                    onChange={() => handleConsent(item.id)}
                    style={{ accentColor: variant === "purple" ? "var(--purple)" : "var(--teal)" }}
                  />
                  <label htmlFor={item.id}
                    dangerouslySetInnerHTML={{
                      __html: isWhatsapp
                        ? item.text
                        : `${item.text} <span class="f-required">*</span>`
                    }} />
                </div>
              );
            })}
          </div>
        )}

        <button
          type="submit"
          className={`lp-submit-btn lp-submit-btn--${variant}`}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : submitLabel}
        </button>
      </form>

      {trustLine && (
        <div className="lp-trust-line">
          <span aria-hidden="true">🔒</span> {trustLine}
        </div>
      )}

      <ToastContainer />
    </div>
  );
}